import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HelpCircle, ChevronDown, ArrowRight, CheckCircle2, X, Lightbulb } from 'lucide-react';
import { useCart } from './CartContext';
import { categories } from '../data/products';
// In-cart material sanity check.
// Asks two quick questions about the project and the customer's top priority,
// then surfaces a recommended material. If the current cart doesn't include
// the recommended material, offers a one-tap swap so customers don't end up
// with the wrong rock for the job.
type Project = 'driveway' | 'patio' | 'garden' | 'drainage' | 'walkway' | 'fill';
type Priority = 'strength' | 'drainage' | 'looks' | 'budget';
type Recommendation = {
  productSlug: string;
  productName: string;
  category: string;
  pricePerYard: number;
  why: string;
};
// Maps a (project, priority) pair to the best-fit product slug + reason.
function recommend(project: Project, priority: Priority): Recommendation | null {
  // Resolve a product by slug into a full Recommendation record.
  const fromSlug = (slug: string, why: string): Recommendation | null => {
    for (const cat of categories) {
      const p = cat.products.find(pp => pp.slug === slug);
      if (p) return {
        productSlug: p.slug,
        productName: p.name,
        category: cat.name,
        pricePerYard: p.pricePerYard,
        why
      };
    }
    return null;
  };
  if (project === 'driveway' || project === 'patio' || project === 'walkway') {
    if (priority === 'budget') return fromSlug('recycled-concrete', 'Most affordable base rock — compacts almost as well as virgin minus.');
    if (priority === 'drainage') return fromSlug('7-8-drain-rock', 'Lets water through quickly while still providing a stable layer.');
    if (priority === 'looks') return fromSlug('5-8-clear', 'Clean, uniform stone — looks neat on surfaces and visible top layers.');
    return fromSlug('5-8-minus', 'Our most popular base — packs hard, supports loads, perfect for driveways and patios.');
  }
  if (project === 'garden') {
    if (priority === 'looks') return fromSlug('medium-bark', 'Refreshes beds, retains moisture, and gives a clean finished look.');
    return fromSlug('3-way-topsoil', 'Balanced soil/sand/compost mix — works for new lawns, beds, and grading.');
  }
  if (project === 'drainage') {
    return fromSlug('7-8-drain-rock', 'Standard rock for French drains, footing drains, and behind walls.');
  }
  // fill / default
  if (priority === 'budget') return fromSlug('recycled-concrete', 'Affordable, compacts well — great for deep fills and sub-base.');
  return fromSlug('fill-dirt', 'Inexpensive bulk material for filling and rough grading.');
}
const projectOptions: {
  id: Project;
  label: string;
}[] = [{
  id: 'driveway',
  label: 'Driveway'
}, {
  id: 'patio',
  label: 'Patio base'
}, {
  id: 'walkway',
  label: 'Walkway'
}, {
  id: 'garden',
  label: 'Garden / lawn'
}, {
  id: 'drainage',
  label: 'Drainage'
}, {
  id: 'fill',
  label: 'Fill / grading'
}];
const priorityOptions: {
  id: Priority;
  label: string;
}[] = [{
  id: 'strength',
  label: 'Strong base'
}, {
  id: 'drainage',
  label: 'Drainage'
}, {
  id: 'looks',
  label: 'Looks good'
}, {
  id: 'budget',
  label: 'Best price'
}];
export function CartHelper() {
  const {
    items,
    addToCart,
    removeFromCart
  } = useCart();
  const [open, setOpen] = useState(false);
  const [project, setProject] = useState<Project | null>(null);
  const [priority, setPriority] = useState<Priority | null>(null);
  const [swapped, setSwapped] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const rec = useMemo(() => project && priority ? recommend(project, priority) : null, [project, priority]);
  // Cart has the recommendation already if any line item's product matches.
  const alreadyHasRec = useMemo(() => {
    if (!rec) return false;
    return items.some(it => it.product.toLowerCase().includes(rec.productName.toLowerCase()) || rec.productName.toLowerCase().includes(it.product.toLowerCase()));
  }, [items, rec]);
  if (dismissed || items.length === 0) return null;
  const handleSwap = () => {
    if (!rec) return;
    // Swap the FIRST cart item to the recommended material — keep its yardage.
    const first = items[0];
    if (!first) return;
    addToCart({
      category: rec.category,
      product: rec.productName,
      quantity: first.quantity,
      pricePerYard: rec.pricePerYard,
      source: first.source || 'cart'
    });
    removeFromCart(first.id);
    setSwapped(true);
  };
  const reset = () => {
    setProject(null);
    setPriority(null);
    setSwapped(false);
  };
  return <div className="mb-5 rounded-2xl bg-amber-50 dark:bg-amber-500/[0.07] border border-amber-200 dark:border-amber-500/20 overflow-hidden" data-id="element-595">
      <button type="button" onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left" aria-expanded={open} data-id="element-596">
        <div className="flex items-center gap-2.5 min-w-0" data-id="element-597">
          <div className="w-8 h-8 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0" data-id="element-598">
            <Lightbulb className="w-4 h-4 text-amber-700 dark:text-amber-300" data-id="element-599" />
          </div>
          <div className="min-w-0" data-id="element-600">
            <p className="text-sm font-bold text-stone-900 dark:text-white truncate" data-id="element-601">
              Not sure this is the right material?
            </p>
            <p className="text-[11px] text-stone-600 dark:text-stone-300 truncate" data-id="element-602">
              Answer 2 quick questions — we'll double-check your pick.
            </p>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-stone-500 dark:text-stone-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-603" />
      </button>

      <AnimatePresence initial={false} data-id="element-604">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="overflow-hidden" data-id="element-605">
            <div className="px-4 pb-4 pt-1 space-y-4 border-t border-amber-200 dark:border-amber-500/20" data-id="element-606">
              {/* Step 1 — Project */}
              <div data-id="element-607">
                <p className="text-[11px] uppercase tracking-wider font-bold text-stone-600 dark:text-stone-300 mb-2" data-id="element-608">
                  Project
                </p>
                <div className="grid grid-cols-3 gap-2" data-id="element-609">
                  {projectOptions.map(opt => {
                const active = project === opt.id;
                return <button key={opt.id} type="button" onClick={() => setProject(opt.id)} className={`px-2 py-2 rounded-lg text-[11px] font-bold transition-colors border ${active ? 'bg-amber-500 text-white border-amber-500' : 'bg-white dark:bg-white/[0.04] border-stone-200 dark:border-white/10 text-stone-700 dark:text-stone-300 hover:border-amber-400'}`} data-id="element-610">
                        {opt.label}
                      </button>;
              })}
                </div>
              </div>

              {/* Step 2 — Priority */}
              <div data-id="element-611">
                <p className="text-[11px] uppercase tracking-wider font-bold text-stone-600 dark:text-stone-300 mb-2" data-id="element-612">
                  Most important
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-613">
                  {priorityOptions.map(opt => {
                const active = priority === opt.id;
                return <button key={opt.id} type="button" onClick={() => setPriority(opt.id)} className={`px-2 py-2 rounded-lg text-[11px] font-bold transition-colors border ${active ? 'bg-amber-500 text-white border-amber-500' : 'bg-white dark:bg-white/[0.04] border-stone-200 dark:border-white/10 text-stone-700 dark:text-stone-300 hover:border-amber-400'}`} data-id="element-614">
                        {opt.label}
                      </button>;
              })}
                </div>
              </div>

              {/* Result */}
              <AnimatePresence data-id="element-615">
                {rec && <motion.div initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.25
            }} className="rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 p-4" data-id="element-616">
                    {swapped ? <div className="flex items-start gap-3" data-id="element-617">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" data-id="element-618" />
                        <div className="min-w-0" data-id="element-619">
                          <p className="text-sm font-bold text-stone-900 dark:text-white" data-id="element-620">
                            Swapped to {rec.productName}
                          </p>
                          <p className="text-[11px] text-stone-600 dark:text-stone-300 mt-0.5" data-id="element-621">
                            Yardage kept the same. Re-check anytime — no harm
                            done.
                          </p>
                          <button type="button" onClick={reset} className="mt-2 text-[11px] font-bold text-amber-700 dark:text-amber-300 hover:underline" data-id="element-622">
                            Run check again
                          </button>
                        </div>
                      </div> : alreadyHasRec ? <div className="flex items-start gap-3" data-id="element-623">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" data-id="element-624" />
                        <div className="min-w-0" data-id="element-625">
                          <p className="text-sm font-bold text-stone-900 dark:text-white" data-id="element-626">
                            You're set — {rec.productName} is the right pick.
                          </p>
                          <p className="text-[11px] text-stone-600 dark:text-stone-300 mt-0.5" data-id="element-627">
                            {rec.why}
                          </p>
                        </div>
                      </div> : <div className="space-y-3" data-id="element-628">
                        <div data-id="element-629">
                          <p className="text-[11px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300 mb-1" data-id="element-630">
                            We'd recommend
                          </p>
                          <p className="text-sm font-bold text-stone-900 dark:text-white" data-id="element-631">
                            {rec.productName}{' '}
                            <span className="font-normal text-stone-500 dark:text-stone-400" data-id="element-632">
                              · ${rec.pricePerYard}/yd
                            </span>
                          </p>
                          <p className="text-[11px] text-stone-600 dark:text-stone-300 mt-1" data-id="element-633">
                            {rec.why}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2" data-id="element-634">
                          <button type="button" onClick={handleSwap} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs transition-colors" data-id="element-635">
                            Swap to {rec.productName}
                            <ArrowRight className="w-3.5 h-3.5" data-id="element-636" />
                          </button>
                          <button type="button" onClick={() => setDismissed(true)} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white text-xs font-medium transition-colors" data-id="element-637">
                            Keep my pick
                          </button>
                        </div>
                      </div>}
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}